import api from './axiosInstance';
import type {
  User,
  AuthTokens,
  ApiResponse,
  SearchHistoryItem,
  SavedRoute,
  Bookmark,
} from '../types';

// ─── Auth ─────────────────────────────────────────────────────────────────

export const registerUser = async (payload: {
  email: string;
  password: string;
}): Promise<{ user: User } & AuthTokens> => {
  const { data } = await api.post<
    ApiResponse<{ user: User } & AuthTokens>
  >('/auth/register', payload);

  return data.data!;
};

export const loginUser = async (payload: {
  email: string;
  password: string;
}): Promise<{ user: User } & AuthTokens> => {
  const { data } = await api.post<
    ApiResponse<{ user: User } & AuthTokens>
  >('/auth/login', payload);

  return data.data!;
};

export const logoutUser = async (): Promise<void> => {
  await api.post('/auth/logout');
};

// ─── User Profile ─────────────────────────────────────────────────────────

export const getProfile = async (): Promise<User> => {
  const { data } = await api.get<ApiResponse<User>>('/user/profile');
  return data.data!;
};

export const updateProfile = async (
  payload: Partial<User>
): Promise<User> => {
  const { data } = await api.put<ApiResponse<User>>(
    '/user/profile',
    payload
  );

  return data.data!;
};

// ─── Search History ───────────────────────────────────────────────────────

export const getSearchHistory = async (): Promise<SearchHistoryItem[]> => {
  const { data } = await api.get<ApiResponse<SearchHistoryItem[]>>(
    '/user/history'
  );

  return data.data || [];
};

// ─── Saved Routes ─────────────────────────────────────────────────────────

export const getSavedRoutes = async (): Promise<SavedRoute[]> => {
  const { data } = await api.get<ApiResponse<SavedRoute[]>>(
    '/user/saved'
  );

  return data.data || [];
};

export const saveRoute = async (payload: {
  fromStation: {
    code: string;
    name: string;
  };
  toStation: {
    code: string;
    name: string;
  };
  label?: string;
}): Promise<SavedRoute> => {
  const { data } = await api.post<ApiResponse<SavedRoute>>(
    '/user/saved',
    payload
  );

  return data.data!;
};

export const deleteSavedRoute = async (
  id: string
): Promise<void> => {
  await api.delete(`/user/saved/${id}`);
};

// ─── Bookmarks ────────────────────────────────────────────────────────────

export const getBookmarks = async (): Promise<Bookmark[]> => {
  const { data } = await api.get<ApiResponse<Bookmark[]>>(
    '/user/bookmarks'
  );

  return data.data || [];
};

export const toggleBookmark = async (payload: {
  trainNumber: string;
  trainName: string;
  fromStation: {
    code: string;
    name: string;
  };
  toStation: {
    code: string;
    name: string;
  };
}): Promise<{ bookmarked: boolean }> => {
  const { data } = await api.post(
    '/user/bookmarks',
    payload
  );

  return {
    bookmarked: data.bookmarked,
  };
};